/**
 * Resolves a companion's `avatarAssetId` (`CompanionView.avatarAssetId`, an
 * opaque id — the edge withholds a bare provider URL) to a fetchable e2 URL. The
 * edge exposes no dedicated avatar-URL field or endpoint today; the ONLY place it
 * presigns an asset's URL is the Artifacts gallery (`GET /companions/:id/gallery`
 * → `MediaAssetView.url`). So this lazily fetches + caches each companion's
 * gallery ONCE, then picks out the one asset matching her `avatarAssetId` — reuse
 * of an existing read, no new backend surface. Degrades to `undefined` (the
 * `Avatar` initials fallback) on a load failure, no match, or an unsignable asset
 * (`url: null`, e.g. before the e2 presigner is wired) — never throws.
 */
import { isErr } from '@4f/shared';
import { createSignal } from 'solid-js';
import type { ApiClient } from '../../api/client.ts';
import type { CompanionView, MediaAssetView } from '../../api/types.ts';

/** Pick the URL of the gallery asset matching `avatarAssetId`, or `undefined`. */
export function findAvatarUrl(
  assets: readonly Pick<MediaAssetView, 'assetId' | 'url'>[],
  avatarAssetId: string | null,
): string | undefined {
  if (!avatarAssetId) {
    return undefined;
  }
  const asset = assets.find((a) => a.assetId === avatarAssetId);
  return asset?.url ?? undefined;
}

export interface AvatarCache {
  /** The resolved avatar URL for a companion id, or `undefined` (initials fallback). */
  srcFor(companionId: string): string | undefined;
  /** Lazily fetch + cache `companion`'s gallery once so `srcFor` can resolve her avatar. */
  ensure(companion: CompanionView): void;
}

/** Build a `Map`-backed cache over the injected client (DIP — no network in tests). */
export function createAvatarCache(api: ApiClient): AvatarCache {
  const [urls, setUrls] = createSignal<ReadonlyMap<string, string>>(new Map());
  const requested = new Set<string>();

  const ensure = (companion: CompanionView): void => {
    if (!companion.avatarAssetId) {
      return;
    }
    // Keyed by (id, avatarAssetId) so promoting a NEW gallery asset to avatar
    // (`setAvatar`) re-fetches instead of serving the prior avatar's cached URL.
    const requestKey = `${companion.id}:${companion.avatarAssetId}`;
    if (requested.has(requestKey)) {
      return;
    }
    requested.add(requestKey);
    void api.listGallery(companion.id).then((result) => {
      // A transient gallery failure OR an asset that is not yet signable
      // (`url: null` before the e2 presigner runs) leaves the avatar unresolved —
      // evict the key so a later `ensure()` (e.g. after `companions.reload()`)
      // retries instead of no-op'ing forever on the initials fallback.
      if (isErr(result)) {
        requested.delete(requestKey);
        return;
      }
      const src = findAvatarUrl(result.value, companion.avatarAssetId);
      if (src) {
        setUrls((current) => new Map(current).set(companion.id, src));
      } else {
        requested.delete(requestKey);
      }
    });
  };

  return {
    srcFor: (companionId) => urls().get(companionId),
    ensure,
  };
}
